import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import getColumns from './getColumns';
import TableLayout from '../../components/shared/TableLayout';
import { Table } from 'antd';
import {
  selectDisplayColumnsForPrivacyPolicy,
  selectPrivacyPolicyWithUIEffect,
  selectPurposeWithUIEffect,
} from '../../redux/store/ui/common';
import {
  fetchPrivacyPoliciesIfNeeded,
  selectDisplayPrivacyPolicy,
  selectPrivacyPolicies,
  selectSelectedPrivacyPolicy,
  selectSelectedPurpose,
} from '../../redux/store/appData/privacyPolicy';
import { PrivacyPolicyData, ProcessingPurposeData } from '../../@types';
import getPurposesColumns from './getPurposesColumns';
import { useIntl } from 'react-intl';
import { getFilteredColumns, getPurposeBaseName } from '../../utils';

// Group processing purposes by base name and activationEventKey
export type GroupedPurpose = {
  baseName: string;
  activationEventKey: string;
  minDuration: string;
  maxDuration: string;
  legalBasis: string;
  collectionNames: string[];
  fieldGroupKeys: string[];
  purposes: ProcessingPurposeData[];
};

const groupProcessingPurposes = (
  purposes: ProcessingPurposeData[]
): GroupedPurpose[] => {
  const grouped: Record<string, GroupedPurpose> = {};

  purposes?.forEach((purpose) => {
    const baseName = getPurposeBaseName(purpose.name || '');
    const key = `${baseName}-${purpose.activationEventKey}`;

    if (!grouped[key]) {
      grouped[key] = {
        baseName,
        activationEventKey: purpose.activationEventKey || '',
        minDuration: purpose.minDuration || '',
        maxDuration: purpose.maxDuration || '',
        legalBasis: purpose.legalBasis || '',
        collectionNames: [purpose.collectionName || ''],
        fieldGroupKeys: [...(purpose.fieldGroupKeys || [])],
        purposes: [purpose],
      };
    } else {
      // Add collection name if not already present
      if (
        !grouped[key].collectionNames.includes(purpose.collectionName || '')
      ) {
        grouped[key].collectionNames.push(purpose.collectionName || '');
      }
      // Merge field groups
      grouped[key].fieldGroupKeys = [
        ...new Set([
          ...grouped[key].fieldGroupKeys,
          ...(purpose.fieldGroupKeys || []),
        ]),
      ];
      grouped[key].purposes.push(purpose);
    }
  });

  return Object.values(grouped);
};

const PrivacyPoliciesTable = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const displayPrivacyPolicies: any[] = useSelector(selectDisplayPrivacyPolicy);
  const privacyPoliciesEntities = useSelector(selectPrivacyPolicies);
  const selectedPrivacyPolicy: any = useSelector(selectSelectedPrivacyPolicy);
  const selectedPurpose: any = useSelector(selectSelectedPurpose);
  const displayColumns = useSelector(selectDisplayColumnsForPrivacyPolicy);
  const selectedId = selectedPrivacyPolicy ? selectedPrivacyPolicy.id : null;
  const columns = getColumns(intl);
  const purposesColumns = getPurposesColumns(intl);
  const filteredColumns = getFilteredColumns(columns, displayColumns);

  const onClickRow = (record: PrivacyPolicyData) => {
    dispatch(selectPrivacyPolicyWithUIEffect(record));
  };

  const onClickGroupedPurposeRow = (record: GroupedPurpose) => {
    // Select the first purpose in the group
    dispatch(selectPurposeWithUIEffect(record.purposes[0]));
  };

  const loadPrivacyPolicies = useCallback(async () => {
    dispatch(fetchPrivacyPoliciesIfNeeded());
  }, []);

  useEffect(() => {
    loadPrivacyPolicies();
  }, []);

  return (
    <TableLayout
      rowKey="pricing"
      columns={filteredColumns}
      dataSource={displayPrivacyPolicies}
      selectedId={selectedId}
      totalNumber={displayPrivacyPolicies?.length}
      onClickRow={onClickRow}
      expandedRowRender={(record) => {
        const groupedPurposes = groupProcessingPurposes(
          record.processingPurposes
        );
        return (
          <Table
            columns={purposesColumns}
            dataSource={groupedPurposes}
            rowKey={(row) => `${row.baseName}-${row.activationEventKey}`}
            pagination={false}
            rowClassName={(row) =>
              row.purposes.some((p) => p.id === selectedPurpose?.id)
                ? 'selected-row'
                : ''
            }
            onRow={(row) => ({
              onClick: (e) => {
                e.preventDefault();
                onClickGroupedPurposeRow(row);
              },
            })}
          />
        );
      }}
    />
  );
};

export default PrivacyPoliciesTable;
